import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StepContainer } from '../../../../StepContainer';
import { useRequestCheckoutContext } from '../../../../CheckoutContext/RequestContext';
import { Slot } from '../../../../AvailableDayPicker/SlotPicker';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { SlotPickerWithPlaceholder } from './SlotPickerWithPlaceholder';
import { useAvailabilitySlots } from '../../../../../../hooks/useAvailability/useAvailabilitySlots';
import { RequestCheckoutState } from '@shared/types';

import {
    Calendar,
    Select,
    CalendarHeader,
    CalendarPagination,
    MonthGrid,
    useSingleDayPickerState,
    useTimezoneDay,
    useTimezoneMonth,
} from '@cademy-ui/calendar';
import Button from '../../../../../Button';
import { ArrowLeftIcon, ArrowRightIcon } from '../../../../../Icons';
import { CademyError } from '@shared/domain-shared';
import Alert from '../../../../../Alert';
import { LinkButton } from '../../../../../LinkButton';

const MINUTE_IN_SECONDS = 60;
const FIFTEEN_MINUTES_IN_SECONDS = MINUTE_IN_SECONDS * 15;

export type AvailableDateStepProps = {};

type DateRange = {
    from: Date;
    to: Date;
};

export const AvailableDateStep: FunctionComponent<AvailableDateStepProps> = () => {
    const { course, educator, checkoutState, submitResponse, selectedDuration } =
        useRequestCheckoutContext();

    const initialDate = useMemo((): Date | undefined => {
        if (checkoutState.stepState.dates.status !== 'complete') {
            return undefined;
        }
        if (checkoutState.stepState.dates.state.type !== 'available date') {
            return new Date(checkoutState.stepState.dates.state.preferredDates[0]) || undefined;
        }
        return new Date(checkoutState.stepState.dates.state.availableDate);
    }, [checkoutState]);

    const duration = useMemo<number>(() => {
        if (!selectedDuration) {
            return FIFTEEN_MINUTES_IN_SECONDS;
        }

        return selectedDuration.duration * MINUTE_IN_SECONDS;
    }, [selectedDuration]);

    const initialSlot = useMemo((): Slot | null => {
        if (initialDate) {
            return {
                from: initialDate,
                to: new Date(initialDate.getTime() + duration * 1000),
            };
        }
        return null;
    }, [initialDate, duration]);

    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(initialSlot);

    const onContinue = useCallback(() => {
        if (!selectedSlot) {
            return;
        }

        submitResponse({
            actionType: 'request available date selected',
            payload: {
                availableDate: selectedSlot?.from.toISOString(),
            },
        });
    }, [selectedSlot, submitResponse]);

    const skipAvailableDates = useCallback(() => {
        submitResponse({
            actionType: 'skip available dates',
            payload: {},
        });
    }, [submitResponse]);

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const optimisticCheckoutState = useMemo((): RequestCheckoutState => {
        return {
            ...checkoutState,
            stepState: {
                ...checkoutState.stepState,
                dates: {
                    status: 'complete',
                    state: {
                        type: 'available date',
                        availableDate: selectedSlot?.from.toISOString() || '',
                    },
                },
            },
        };
    }, [checkoutState, selectedSlot]);

    const continueDisabled = useMemo(() => {
        if (selectedSlot) {
            return false;
        }

        return true;
    }, [selectedSlot]);

    const {
        date: selectedDayDate,
        isDaySelected,
        onDaySelected,
    } = useSingleDayPickerState(initialDate);
    const { range: selectedDayRange } = useTimezoneDay(
        selectedDayDate || new Date(),
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const { range: initialMonthRange } = useTimezoneMonth(
        selectedDayDate || new Date(),
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    const isDateInSelectedDay = useCallback(
        (date: Date) => {
            if (!selectedDayDate) {
                return false;
            }
            return (
                date.getTime() >= selectedDayRange.from.getTime() &&
                date.getTime() < selectedDayRange.to.getTime()
            );
        },
        [selectedDayDate, selectedDayRange]
    );

    const [dateRange, setDateRange] = useState<DateRange>({
        from: initialMonthRange.from,
        to: initialMonthRange.to,
    });

    const {
        data: availableMonthSlots,
        isLoading,
        error: availableSlotsError,
    } = useAvailabilitySlots(
        {
            courseSlug: course.slug,
            educatorSlug: educator.slug,
            durationInSeconds: duration,
            from: dateRange.from,
            to: dateRange.to,
        },
        {
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    const [availableSlots, setAvailableSlots] = useState<Array<Slot>>([]);

    const error: CademyError | null = useMemo(() => {
        if (!availableSlotsError) {
            return null;
        }
        return CademyError.fromUnknown(availableSlotsError);
    }, [availableSlotsError]);

    useEffect(() => {
        if (!selectedDayDate) {
            return setAvailableSlots([]);
        }
        if (!availableMonthSlots) {
            return;
        }
        const isSelectedDateInRange =
            selectedDayDate.getTime() >= dateRange.from.getTime() &&
            selectedDayDate.getTime() < dateRange.to.getTime();
        if (isSelectedDateInRange) {
            setAvailableSlots(
                availableMonthSlots.filter((slot) => {
                    return isDateInSelectedDay(slot.from) && slot.from.getTime() > Date.now();
                })
            );
        }
    }, [isDateInSelectedDay, selectedDayDate, availableMonthSlots, dateRange]);

    const isDayDisabled = useCallback(
        ({ from, to }: { from: Date; to: Date }) => {
            if (to.getTime() < Date.now()) {
                return true;
            }
            if (!availableMonthSlots) {
                return true;
            }
            return (
                availableMonthSlots.some((slot) => {
                    const slotIsInRange =
                        slot.from.getTime() >= from.getTime() && slot.from.getTime() < to.getTime();
                    const slotHasNotPassed = slot.from.getTime() >= Date.now();
                    return slotIsInRange && slotHasNotPassed;
                }) === false
            );
        },
        [availableMonthSlots]
    );

    const disablePreviousMonthButton = useCallback((previousMonthEnd: Date) => {
        return previousMonthEnd < new Date();
    }, []);

    return (
        <StepContainer
            course={course}
            optimisticCheckoutState={optimisticCheckoutState}
            continueDisabled={continueDisabled}
            onContinueClick={onContinue}
            onBackClick={onBackClick}
        >
            <h2>Select a Date & Time</h2>
            {error ? (
                <Alert preset="danger">
                    <span>{error.detail || error.title}</span>
                    <br />
                    <br />
                    <span>
                        It looks like we&apos;re having problems checking the availability on this
                        course, you can try refreshing the page, trying again later or{' '}
                        <LinkButton onClick={skipAvailableDates}>
                            let us know which dates work for you and we&apos;ll get back to you to
                            book you in.
                        </LinkButton>
                    </span>
                </Alert>
            ) : null}
            <div className={styles.container}>
                <div className={styles.column}>
                    <Calendar>
                        <CalendarHeader />
                        <Select
                            isDaySelected={isDaySelected}
                            onDaySelected={onDaySelected}
                            isDayDisabled={isDayDisabled}
                        >
                            <MonthGrid
                                className={classNames({ [styles.isLoading]: isLoading })}
                                onVisibleMonthChanged={setDateRange}
                            />
                        </Select>
                        <CalendarPagination
                            PreviousButton={({ onClick, previousMonthEnd }) => {
                                return (
                                    <Button
                                        name="Previous month"
                                        preset="textButton"
                                        onClick={onClick}
                                        disabled={disablePreviousMonthButton(previousMonthEnd)}
                                    >
                                        <ArrowLeftIcon />
                                    </Button>
                                );
                            }}
                            NextButton={({ onClick }) => {
                                return (
                                    <Button name="Next month" preset="textButton" onClick={onClick}>
                                        <ArrowRightIcon />
                                    </Button>
                                );
                            }}
                        />
                    </Calendar>
                </div>
                <div
                    className={classNames(styles.column, {
                        [styles.hasSelectedDay]: Boolean(selectedDayDate),
                    })}
                >
                    <header className={styles.slotsHeader}>Select a time:</header>
                    <SlotPickerWithPlaceholder
                        slots={availableSlots}
                        onClick={setSelectedSlot}
                        value={selectedSlot}
                        selectedDay={selectedDayDate}
                    />
                </div>
            </div>
        </StepContainer>
    );
};
